import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'nfa-owned-items';

export function useOwnedItems() {
  const [ownedItems, setOwnedItems] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    } catch {
      // Invalid data, start fresh
    }
    return new Set();
  });

  // Persist to localStorage whenever ownedItems changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...ownedItems]));
    } catch {
      // localStorage might be full or disabled
    }
  }, [ownedItems]);

  const toggleOwned = useCallback((productId: string) => {
    setOwnedItems((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  const isOwned = useCallback(
    (productId: string) => ownedItems.has(productId),
    [ownedItems]
  );

  const clearOwned = useCallback(() => {
    setOwnedItems(new Set());
  }, []);

  const setOwned = useCallback((productIds: string[]) => {
    setOwnedItems(new Set(productIds));
  }, []);

  const getOwnedArray = useCallback(() => [...ownedItems], [ownedItems]);

  return {
    ownedItems,
    toggleOwned,
    isOwned,
    clearOwned,
    setOwned,
    getOwnedArray,
  };
}
